import classNames from "classnames";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { ID } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import React, { useState, useEffect } from "react";
import { useStore } from "../../models";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { Draggable } from "../../components/draggable";
import { InboxEntry } from "../../components/inbox/inboxEntry";
import { InboxPlaceholder } from "../../components/inbox/inboxPlaceholder";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { IInboxEntry } from "../../models/inbox/InboxEntry";
import { InboxFilterId } from "../../models/inbox/InboxFilter";
import { Filters } from "./filter/filters";
import { logger } from "@fidget/common/utils";
import styles from "./inbox.css";
import { ShowMoreIcon } from "@fidget/common/components/icons/showMoreIcon/showMoreIcon";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../models/agentServices";

export const InboxContainer = observer(() => {
  const {
    displayedEntries,
    remainingEntries,
    snoozedEntries,
    entryElements,
    setEntryElement,
    handleClearSearchClick,
    onScroll,
    isDisplayingAll,
    toggleDisplayAll,
    filter,
    inboxStore,
    calendarStore,
    routerStore,
    uiStore
  } = useInboxContainerState();

  const renderEntries = () => {
    const numberPlaceholders =
      filter.id === InboxFilterId.YOURS ? staticConfig.inboxYoursPlaceholderNumber : undefined;
    return (
      <>
        {displayedEntries.length > 0 ? (
          displayedEntries.map((entry: IInboxEntry) => entry && renderEntry(entry))
        ) : (
          <InboxPlaceholder
            text={uiStore.canDisplayConversations() ? filter.placeholderText : undefined}
            numberElements={numberPlaceholders}
          />
        )}
        <div className={classNames(styles.remaining, { [styles.showing]: isDisplayingAll })}>
          {remainingEntries.map((entry: IInboxEntry) => entry && renderEntry(entry))}
        </div>
        {remainingEntries.length > 0 && (
          <div className={styles["show-more-container"]}>
            <ShowMoreIcon
              onClick={toggleDisplayAll}
              text={
                isDisplayingAll ? intl.formatMessage("showLess") : intl.formatMessage("showMore")
              }
            />
          </div>
        )}
        {snoozedEntries.length > 0 && (
          <>
            <div
              className={classNames(
                styles["inbox-message-container"],
                styles["snoozed-conversations"]
              )}
            >
              <i className={"icon-snooze"} />
              <L10n id={"inbox.snoozedConversations"} />
            </div>
            {snoozedEntries.map((entry: IInboxEntry) => renderEntry(entry))}
          </>
        )}
      </>
    );
  };

  const renderEntry = (entry: IInboxEntry) => {
    return (
      <div
        key={entry.id}
        className={styles["inbox-message-container"]}
        ref={(el) => setEntryElement(entry.id, el as HTMLDivElement)}
      >
        <Draggable
          onClick={() =>
            inboxStore.searchResults.length > 0
              ? routerStore.goToRoom(entry.id)
              : filter.selectEntry(entry.id)
          }
          onDragStart={(start, offset) => {
            const el = entryElements.get(entry.id);
            if (el && entry.owner.hasContactData) {
              calendarStore.calendarEntryDragStarted(start, offset, el.clientWidth, entry.id);
            }
          }}
          onDragEnd={() => calendarStore.calendarEntryDragEnded()}
        >
          <InboxEntry key={entry.id} entry={entry} />
        </Draggable>
      </div>
    );
  };

  const render = () => {
    return (
      <div className={styles.inbox}>
        {inboxStore.searchResults.length > 0 ? (
          <header className={styles["search-results-header"]}>
            <p>
              <L10n id="inbox.search.header" />
            </p>
            <button type="button" onClick={handleClearSearchClick}>
              <i className={styles["clear-search-icon"]} />
            </button>
          </header>
        ) : (
          <Filters />
        )}
        <div className={styles["inbox-messages-wrap"]} onScroll={onScroll}>
          {renderEntries()}
        </div>
        <div
          className={classNames(styles.loader, { [styles.visible]: filter.isLoadingNextEntries })}
        >
          <CloserLoaderComponent width={"40px"} height={"40px"} />
        </div>
      </div>
    );
  };

  return render();
});

const useInboxContainerState = () => {
  const { spinnerService } = useAgentServices();
  const { inboxStore, routerStore, calendarStore, currentUserStore, uiStore } = useStore();

  const [entryElements] = useState<Map<ID, HTMLDivElement>>(new Map());
  const [isDisplayingAll, setIsDisplayingAll] = useState(false);
  const [displayedEntries, setDisplayedEntries] = useState<IInboxEntry[]>([]);
  const [remainingEntries, setRemainingEntries] = useState<IInboxEntry[]>([]);

  const filter = inboxStore.selectedFilter;

  const splitEntries = () => {
    const limit = currentUserStore.orgConfig?.autoAssignedLimit;
    if (!limit) {
      setDisplayedEntries(inboxStore.displayedEntries);
      setRemainingEntries([]);

      return;
    }

    setDisplayedEntries(inboxStore.displayedEntries.slice(0, limit));
    setRemainingEntries(inboxStore.displayedEntries.slice(limit));
  };

  useEffect(() => {
    if (filter.id !== InboxFilterId.YOURS) {
      setDisplayedEntries(inboxStore.displayedEntries);
      setRemainingEntries([]);
    } else {
      splitEntries();
    }
  }, [inboxStore.displayedEntries]);

  useEffect(() => {
    const fetchPreferenceSorting = async () => {
      try {
        if (inboxStore.currentUserProfile) {
          const { id } = inboxStore.currentUserProfile;
          if (id) {
            const preferences = await spinnerService.getAgentPreferences(id);
            const { inboxSorting } = preferences;
            if (inboxSorting) {
              inboxStore.selectSorting(inboxSorting);
            }
          }
        }
      } catch (error) {
        logger.error({ message: "Couldn't get agent sorting preferences", error });
      }
    };

    fetchPreferenceSorting();

    return () => {
      onScrollDebounced.cancel();
    };
  }, []);

  const onScroll = (event: React.UIEvent<HTMLDivElement>) =>
    onScrollDebounced(event.target as HTMLDivElement);

  const onScrollDebounced = debounce(
    (container: HTMLDivElement) => {
      handleScroll(container);
    },
    100,
    { leading: true }
  );

  const setEntryElement = (chatId?: ID, el?: HTMLDivElement) => {
    if (chatId && el) {
      entryElements.set(chatId, el);
    }
  };

  const handleClearSearchClick = () => {
    inboxStore.clearSearch();
  };

  const handleScroll = (container: HTMLDivElement) => {
    if (filter.hasMoreEntries && container) {
      const { scrollHeight, offsetHeight, scrollTop } = container;
      const scrollContainerBottom = scrollHeight - offsetHeight - scrollTop;
      if (scrollContainerBottom <= 10) {
        filter.loadNextEntries();
      }
    }
  };

  return {
    displayedEntries,
    remainingEntries,
    snoozedEntries: inboxStore.snoozedEntries,
    filter,
    entryElements,
    setEntryElement,
    handleClearSearchClick,
    onScroll,
    isDisplayingAll,
    toggleDisplayAll: () => setIsDisplayingAll(!isDisplayingAll),
    routerStore,
    calendarStore,
    inboxStore,
    uiStore
  };
};
